import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Users, Plus, Trash2, Edit, Key, Shield,
  UserCog, Eye, Loader2, AlertTriangle, CheckCircle, RefreshCw, Send, X
} from 'lucide-react';
import { toast } from 'sonner';
import { formatBackendDate } from '../../lib/dateUtils';
import API_BASE from '../../utils/apiBase';
import { normalizePrimaryWorkArea, USER_DEPARTMENTS } from '../../config/portalNavigation';

const API = API_BASE;

const ROLE_CONFIG = {
  workspace_owner: { label: 'Workspace Owner', color: 'bg-emerald-100 text-emerald-700', icon: Shield },
  workspace_admin: { label: 'Workspace Admin', color: 'bg-blue-100 text-blue-700', icon: Shield },
  registered_manager: { label: 'Registered Manager', color: 'bg-indigo-100 text-indigo-700', icon: Shield },
  admin: { label: 'Workspace Admin', color: 'bg-blue-100 text-blue-700', icon: Shield },
  branch_manager: { label: 'Branch Manager', color: 'bg-green-100 text-green-700', icon: UserCog },
  manager: { label: 'Branch Manager (legacy)', color: 'bg-green-100 text-green-700', icon: UserCog },
  auditor: { label: 'Auditor', color: 'bg-amber-100 text-amber-700', icon: Eye }
};

const STAFF_ROLES = [
  ['workspace_admin', 'Workspace Admin', 'Broad workspace administration.'],
  ['registered_manager', 'Registered Manager', 'Service oversight and governance.'],
  ['branch_manager', 'Branch Manager', 'Branch operations and staffing.'],
  ['auditor', 'Auditor', 'Read-only quality and audit visibility.'],
];

const ROLE_WORK_AREA_DEFAULTS = {
  workspace_admin: 'organisation_wide',
  registered_manager: 'management_governance',
  branch_manager: 'operations_staffing',
  auditor: 'compliance_quality',
};

const workAreaLabel = (value) => {
  const normalized = normalizePrimaryWorkArea(value);
  return USER_DEPARTMENTS.find((area) => area.id === normalized)?.label || normalized.replace(/_/g, ' ');
};

const planLabel = (planId) => String(planId || 'essential').replace(/^./, (c) => c.toUpperCase());

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [workspaceOwner, setWorkspaceOwner] = useState(null);
  const [activeStaff, setActiveStaff] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [inactiveInvitations, setInactiveInvitations] = useState([]);
  const [seatUsage, setSeatUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [roleEntitlements, setRoleEntitlements] = useState({
    current_plan_id: 'essential',
    allowed_staff_roles: ['workspace_admin'],
    locked_staff_roles: [],
  });
  const [workAreaManuallySelected, setWorkAreaManuallySelected] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'workspace_admin',
    department: '',
  });
  const [newPassword, setNewPassword] = useState('');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get current user
      const meResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(meResponse.data);
      
      // Get all admin users
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
      setWorkspaceOwner(response.data.workspace_owner || null);
      setActiveStaff(response.data.active_staff || response.data.users || []);
      setPendingInvitations(response.data.pending_invitations || []);
      setInactiveInvitations(response.data.inactive_invitations || []);
      setSeatUsage(response.data.seat_usage || null);
      setRoleEntitlements(response.data.role_entitlements || {
        current_plan_id: 'essential',
        allowed_staff_roles: ['workspace_admin'],
        locked_staff_roles: [],
      });
    } catch (error) {
      console.error('Failed to fetch users:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view admin users');
      } else {
        toast.error('Failed to load admin users');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const resetForm = () => {
    setFormData({ email: '', name: '', role: 'workspace_admin', department: '' });
    setWorkAreaManuallySelected(false);
    setNewPassword('');
  };

  const handleCreateUser = async () => {
    if (!formData.email || !formData.name || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/admin/users/invitations`, {
        ...formData,
        department: formData.department || ROLE_WORK_AREA_DEFAULTS[formData.role] || 'organisation_wide',
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Staff invitation prepared');
      setShowCreateDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleInvitationAction = async (invitationId, action) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/invitations/${invitationId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(action === 'revoke' ? 'Invitation revoked' : 'Invitation updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} invitation`);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/admin/users/${selectedUser.user_id}`, {
        name: formData.name,
        role: formData.role,
        department: normalizePrimaryWorkArea(formData.department),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User updated successfully');
      setShowEditDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API}/admin/users/${selectedUser.user_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User deleted successfully');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    if (newPassword.length < 12) {
      toast.error('Password must be at least 12 characters and include uppercase, lowercase, a number and a symbol');
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/admin/users/${selectedUser.user_id}/reset-password`, {
        password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password reset successfully');
      setShowResetPasswordDialog(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      role: user.workspace_role || (user.role === 'manager' ? 'branch_manager' : user.role),
      department: normalizePrimaryWorkArea(user.department),
    });
    setWorkAreaManuallySelected(true);
    setShowEditDialog(true);
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isTenantUserRole = (role) => ['admin', 'branch_manager', 'manager', 'auditor', 'super_admin'].includes(role);
  const tenantUsers = activeStaff.length ? activeStaff : users.filter((user) => isTenantUserRole(user.role) && user.workspace_role !== 'workspace_owner');
  const adminSeatLimit = Number(currentUser?.workspace_entitlements?.limits?.admin_users_limit || 0);
  const adminSeatsUsed = Number(seatUsage?.used || currentUser?.workspace_usage?.admin_users || tenantUsers.length || 0);
  const adminSeatsFull = adminSeatLimit > 0 && adminSeatsUsed >= adminSeatLimit;
  const allowedStaffRoles = new Set(roleEntitlements.allowed_staff_roles || ['workspace_admin']);
  const lockedRoleMap = new Map((roleEntitlements.locked_staff_roles || []).map((role) => [role.role, role]));
  const selectableRoles = STAFF_ROLES.filter(([value]) => allowedStaffRoles.has(value));
  const lockedRoles = STAFF_ROLES.filter(([value]) => lockedRoleMap.has(value));
  const currentPlanLabel = planLabel(roleEntitlements.current_plan_id);
  const selectedWorkArea = formData.department || ROLE_WORK_AREA_DEFAULTS[formData.role] || 'organisation_wide';
  const handleRoleChange = (role) => {
    setFormData((current) => ({
      ...current,
      role,
      department: workAreaManuallySelected ? current.department : ROLE_WORK_AREA_DEFAULTS[role] || 'organisation_wide',
    }));
  };
  const handleWorkAreaChange = (value) => {
    setWorkAreaManuallySelected(true);
    setFormData({ ...formData, department: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Access & Permissions</h1>
          <p className="text-sm text-gray-500 mt-1">
            Invite staff members and manage active tenant access
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchUsers} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {isSuperAdmin && (
            <Button
              onClick={() => { resetForm(); setShowCreateDialog(true); }}
              disabled={adminSeatsFull}
              title={adminSeatsFull ? 'This subscription has no free staff access seats' : 'Invite staff member'}
            >
              <Plus className="h-4 w-4 mr-2" />
              Invite staff member
            </Button>
          )}
        </div>
      </div>

      {!isSuperAdmin && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Limited Access</p>
            <p className="text-sm text-amber-700">
              Only the workspace owner can create, edit, or delete staff access.
              Contact your workspace owner if you need to manage users.
            </p>
          </div>
        </div>
      )}

      <div className={`rounded-lg border p-4 flex items-start gap-3 ${
        adminSeatsFull ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'
      }`}>
        <Shield className={`h-5 w-5 mt-0.5 ${adminSeatsFull ? 'text-amber-700' : 'text-emerald-700'}`} />
        <div>
          <p className={`font-medium ${adminSeatsFull ? 'text-amber-900' : 'text-emerald-900'}`}>
            Staff access seats: {adminSeatsUsed}{adminSeatLimit ? `/${adminSeatLimit}` : ''}
          </p>
          <p className={`text-sm ${adminSeatsFull ? 'text-amber-800' : 'text-emerald-800'}`}>
            {adminSeatsFull
              ? 'This subscription has no free staff access seats. Upgrade or revoke a pending invitation before inviting another staff member.'
              : 'The Workspace Owner is separate. Pending staff invitations reserve a staff access seat until they are accepted, revoked or expired.'}
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <p className="text-sm font-semibold text-slate-900">Current plan: {currentPlanLabel}</p>
        <p className="mt-1 text-sm text-slate-700">
          Roles control what each person is allowed to do. Primary work area controls which parts of the workspace are prioritised in their navigation.
          Work area never grants permissions beyond the assigned role.
        </p>
        <p className="mt-1 text-xs text-slate-600">Some roles may require an upgrade.</p>
      </div>

      {isSuperAdmin && (
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-700" />
              Workspace owner
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4 rounded-lg border border-emerald-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-semibold text-gray-900">{workspaceOwner?.name || currentUser?.name || 'Workspace Owner'}</p>
                <p className="text-sm text-gray-600">{workspaceOwner?.email || currentUser?.email}</p>
              </div>
              <Badge className="w-fit bg-emerald-100 text-emerald-800">Workspace Owner</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Active staff access ({tenantUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tenantUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No active staff access found
            </div>
          ) : (
            <div className="space-y-3">
              {tenantUsers.map((user) => {
                const displayRole = user.workspace_role || user.role;
                const roleConfig = ROLE_CONFIG[displayRole] || ROLE_CONFIG[user.role] || ROLE_CONFIG.admin;
                const RoleIcon = roleConfig.icon;
                const isCurrentUser = user.user_id === currentUser?.user_id;
                
                return (
                  <div 
                    key={user.user_id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      isCurrentUser ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${roleConfig.color}`}>
                        <RoleIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{user.name}</span>
                          {isCurrentUser && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Badge className={roleConfig.color}>
                        {roleConfig.label}
                      </Badge>
                      {user.department ? (
                        <Badge variant="outline" className="text-xs capitalize">
                          {workAreaLabel(user.department)}
                        </Badge>
                      ) : null}
                      <span className="text-xs text-gray-400">
                        Created {formatBackendDate(user.created_at)}
                      </span>
                      
                      {isSuperAdmin && !isCurrentUser && (
                        <div className="flex gap-1 ml-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openEditDialog(user);
                            }}
                            title="Edit user"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedUser(user);
                              setShowResetPasswordDialog(true);
                            }}
                            title="Emergency password reset"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedUser(user);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete user"
                            data-testid={`delete-admin-user-${user.user_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            Pending invitations ({pendingInvitations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingInvitations.length === 0 ? (
            <div className="text-center py-6 text-gray-500">No pending invitations</div>
          ) : (
            <div className="space-y-3">
              {pendingInvitations.map((invite) => (
                <div key={invite.id} className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{invite.contact_name}</p>
                    <p className="text-sm text-gray-500">{invite.contact_email}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {invite.role_label || ROLE_CONFIG[invite.workspace_role]?.label || invite.workspace_role} | {formatBackendDate(invite.expires_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="capitalize">{String(invite.status || '').replace(/_/g, ' ')}</Badge>
                    <Button type="button" size="sm" variant="outline" onClick={() => handleInvitationAction(invite.id, invite.status === 'draft' ? 'send' : 'resend')} disabled={saving}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {invite.status === 'draft' ? 'Send' : 'Resend'}
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => handleInvitationAction(invite.id, 'revoke')} disabled={saving}>
                      <X className="mr-2 h-4 w-4" />
                      Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <details className="rounded-lg border border-gray-200 bg-white">
        <summary className="cursor-pointer px-6 py-4 font-semibold text-gray-900">
          Revoked and expired invitations ({inactiveInvitations.length})
        </summary>
        <div className="space-y-2 border-t border-gray-100 p-4">
          {inactiveInvitations.length === 0 ? (
            <p className="text-sm text-gray-500">No inactive invitations</p>
          ) : inactiveInvitations.map((invite) => (
            <div key={invite.id} className="flex items-center justify-between rounded-md border border-gray-100 p-3 text-sm">
              <span>{invite.contact_name} | {invite.contact_email}</span>
              <Badge variant="outline" className="capitalize">{String(invite.status || '').replace(/_/g, ' ')}</Badge>
            </div>
          ))}
        </div>
      </details>

      {/* Invite Staff Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite staff member</DialogTitle>
            <DialogDescription>
              Send a secure invitation. The invitee chooses their own password.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="John Smith"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="admin@osabeacares.co.uk"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="role">Role *</Label>
              <Select 
                value={formData.role}
                onValueChange={handleRoleChange}
              >
                <SelectTrigger id="role" className="mt-1">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {selectableRoles.map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {lockedRoles.length ? (
                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="font-semibold">More roles available on higher plans</p>
                      <ul className="mt-2 space-y-1">
                        {lockedRoles.map(([value, label]) => {
                          const lock = lockedRoleMap.get(value);
                          return (
                            <li key={value}>
                              <span className="font-medium">{label}</span>
                              <span className="text-amber-800">
                                {' '}
                                - Available on {(lock?.upgrade_plan_required || 'enterprise').replace(/^./, (c) => c.toUpperCase())}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    <Button type="button" variant="outline" className="bg-white" onClick={() => navigate('/portal/plans-billing')}>
                      Upgrade plan
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="grid gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              {STAFF_ROLES.map(([value, label, description]) => (
                <div key={value} className="text-sm">
                  <span className="font-semibold text-slate-900">{label}: </span>
                  <span className="text-slate-600">{description}</span>
                  {lockedRoleMap.has(value) ? (
                    <span className="ml-2 font-medium text-amber-700">
                      Available on {(lockedRoleMap.get(value)?.upgrade_plan_required || 'enterprise').replace(/^./, (c) => c.toUpperCase())}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>

            <div>
              <Label htmlFor="department">Primary work area (optional)</Label>
              <Select
                value={selectedWorkArea}
                onValueChange={handleWorkAreaChange}
              >
                <SelectTrigger id="department" className="mt-1">
                  <SelectValue placeholder="Select primary work area" />
                </SelectTrigger>
                <SelectContent>
                  {USER_DEPARTMENTS.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Prioritises navigation and dashboard links only. Permissions still come from the selected role.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Prepare invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-role">Role</Label>
              <Select 
                value={formData.role}
                onValueChange={(v) => setFormData({...formData, role: v})}
              >
                <SelectTrigger id="edit-role" className="mt-1">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {selectableRoles.map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {lockedRoles.length ? (
                <p className="mt-2 text-xs text-amber-700">
                  Locked roles require a plan upgrade before active access can be changed.
                </p>
              ) : null}
            </div>

            <div>
              <Label htmlFor="edit-department">Primary work area (optional)</Label>
              <Select
                value={normalizePrimaryWorkArea(formData.department)}
                onValueChange={handleWorkAreaChange}
              >
                <SelectTrigger id="edit-department" className="mt-1">
                  <SelectValue placeholder="Select primary work area" />
                </SelectTrigger>
                <SelectContent>
                  {USER_DEPARTMENTS.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUser} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-admin-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Emergency password reset</DialogTitle>
            <DialogDescription>
              Use only when an existing active account cannot recover access. New staff should use invitations and choose their own password.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              className="mt-1"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasswordDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} disabled={saving || newPassword.length < 12}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

